import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Toolbar,
  Tooltip,
  Typography,
  Box,
} from '@mui/material'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import TableHeading from './Components/TableHeading'
import TableRowWrapper from './Components/TableRowWrapper'
import TableFooter from './Components/TableFooter'
import {
  useAllValues,
  useCommentValues,
  useComputedData,
  useFieldValidationError,
  useNoValidationValues,
  useProcessContext,
  useReviewValues,
  useSelectedTableData,
  useTableSequenceAndHeaders,
  useTableStructure,
} from '../../../../store'
import { useAuthContext } from '../../../../hooks/useAuthContext'
import {
  convertFromLegacyReviewToRowWiseComment,
  convertFromLegacyReviewToRowWiseReview,
  convertFromLegacyToRowWiseData,
  getAllRows,
} from '../../utils/tableFieldUtil'
import {
  getCommentId,
  getFieldId,
  getFieldIdFromKey,
  getLegacySubfieldId,
  getLegacySubfieldReviewId,
  getReviewId,
  getTableFieldId,
  getTableRowId,
  removeTableStructureData,
} from '../../utils/utils'
import { autoComputeFieldValue } from '../../utils/autoCompute'
import { processDataService } from '../../../../services/processDataService'
import {
  ACTIVITY_TYPES,
  FIELD_TYPES,
  REVIEW_CONTEXTS,
  TABLE_ADD_ROW_TYPE,
  TABLE_CONFIG_CONTEXTS,
  TABLE_CONTEXTS,
} from '../../../../utility/constants'
import TableSetting from '../../../../components/Dashboard/PreviewComponent/TableSettings/TableSetting'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import {
  checkFieldConstraints,
  getDefaultValueForField,
  getFieldForSetting,
  isConditionallyEnabled,
  isFieldHidden,
} from '../../utils/fieldUtil'
import { userConfig } from '../../../../services/userConfigServices'
import { getHiddenId } from '../../../../components/Dashboard/PreviewComponent/TableSettings/utils'
import { getFilteredTableData } from '../../utils/filterUtils'
import { getTimeInMillis } from '../../../../utility/datetime'
import {
  syncChildParentReviewRecursively,
  syncChildRecursively,
  syncWithParent,
} from '../../utils/constraintUtils'
import { fieldTemplateService } from '../../../../services/fieldTemplateService'
import TableRowHeader from './Components/TableRowHeader'
import { getSubfieldDefaultValue } from './tableUtil'
import _ from 'lodash'
import { constraintValidationWithOperation } from '../../utils/validationUtil'
import { useResponsiveTypography } from '../../../../utility/responsiveTypography'
import { UploadFile } from '@mui/icons-material'
import UploadExcelDataExtended from '../../Components/UploadExcelDataExtended'

export const ON_CHANGE_DATA_TYPE = {
  REVIEW_DATA: 'review-data',
  COMMENT_DATA: 'comment-data',
  VALUE_DATA: 'value-data',
}

/* 
  data: {
    f_{table_id}: lastRowId,us
    tf_{table_id}: {
      tr_{row_id}: {
        f_{field_id}: value
      }
    }
  }
// */

export const MIN_ROWS_TO_VIRTUALIZE_TABLE = 30

const TableField = ({ field, fields, disabled = false }) => {
  const [freezeColumnId, setFreezeColumnId] = useState(null)

  const { dataEntryPageTitle } = useResponsiveTypography()

  // virtualization variables
  const containerRef = useRef()
  const [startIndex, setStartIndex] = useState(0)
  const [endIndex, setEndIndex] = useState(1)
  const [elHeight, setElHeight] = useState(null)
  const scrollTimeoutRef = useRef(null)
  const [containerHeight, setContainerHeight] = useState(400)

  // some contexts and variables
  const { user } = useAuthContext()
  const processContext = useProcessContext((state) => state.processContext)
  const allFields = useProcessContext((state) => state.processContext.allFields)
  const listDataMetadata = useProcessContext((state) => state.processContext.listDataMetadata)
  const conditionalEnableDisable = processContext?.conditionalEnableDisable
  const activityType = processContext?.activityTemplate?.activity_type
  const teamsList = useProcessContext((state) => state.processContext.dynamicListData.teamsList)

  // computed data
  const computedData = useComputedData((state) => state.computedData)
  const setComputedData = useComputedData((state) => state.setComputedData)

  /* Initial State: allValues, ReviewComment, ReviewData */
  const {
    allValues,
    getAllValues,
    setInitialAllValues,
    updateRowValues,
    setAllValues,
    getFormUpdatedAt,
  } = useAllValues()
  const formUpdatedAt = getFormUpdatedAt()

  const reviewValues = useReviewValues((state) => state.reviewValues) // reviewValues[getTableFieldId(field.id)]
  const setReviewValues = useReviewValues((state) => state.setReviewValues) // setReviewValues({...reviewValues, [getTableFieldId(field.id)]: data })
  const commentValues = useCommentValues((state) => state.commentValues)
  const setCommentValues = useCommentValues((state) => state.setCommentValues)
  const selectedTableData = useSelectedTableData((state) => state.selectedTableData)

  const setTableStructure = useTableStructure((state) => state.setTableStructure)

  /* ========================== */

  /* all reviews */
  const reviewData = useReviewValues((state) =>
    state.reviewValues[getTableFieldId(field.id)]
      ? state.reviewValues[getTableFieldId(field.id)]
      : {},
  )

  const updateReviewDataRow = useReviewValues((state) => state.updateReviewDataRow)

  /* all comments */
  const commentData = useCommentValues((state) =>
    state.commentValues[getTableFieldId(field.id)]
      ? state.commentValues[getTableFieldId(field.id)]
      : {},
  )
  const updateCommentDataRow = useCommentValues((state) => state.updateCommentDataRow)

  const fieldTemplateDep = useProcessContext((state) => state.processContext.fieldTemplateDep)
  const listDataDep = useProcessContext((state) => state.processContext.listDataDep)
  const userTeamMap = useProcessContext((state) => state.processContext.userTeamMap)
  const activityTemplate = useProcessContext((state) => state.processContext.activityTemplate)

  const version = processContext?.processData?.version

  const { setNoValidationValues, removeFromNoValidationValues, getNoValidationValues } =
    useNoValidationValues()

  const { unSelectRow, selectRow, getTableSelectedRow } = useSelectedTableData()
  const currentSelectedRowData = getTableSelectedRow(field.id)

  const fieldValidationError = useFieldValidationError((state) => state.fieldValidationError)
  const setFieldValidationError = useFieldValidationError((state) => state.setFieldValidationError)

  const fieldConstraints = useProcessContext((state) => state.processContext.fieldConstraints)

  const tableSequenceAndHeaders = useTableSequenceAndHeaders(
    (state) => state.tableSequenceAndHeaders,
  )
  const setTableSequenceAndHeaders = useTableSequenceAndHeaders(
    (state) => state.setTableSequenceAndHeaders,
  )

  /* ========================== */

  /* table toolbar */
  const [fetchedConfig, setFetchedConfig] = useState(false)
  const [hiddenColumns, setHiddenColumns] = useState(null)
  const [filterEnabled, setFilterEnabled] = useState(false)
  const [filterValues, setFilterValues] = useState({})

  const [openExcelUpload, setOpenExcelUpload] = useState(false)
  const highestRowRef = useRef(0)

  /* rerender complete field when changing hidden columns from setting */
  useEffect(() => {
    setAllValues({ updatedAt: getTimeInMillis() })
  }, [hiddenColumns])

  useEffect(() => {
    highestRowRef.current = allValues[getFieldId(field.id)] || 0
  }, [allValues, field.id])

  /* columns and rows */
  const [columns] = useState(
    processContext?.tableSubfields?.find((it) => it.id == field.id)?.subfields,
  )

  const [allRows, setAllRows] = useState([1])
  const [allRowsWithHeaders, setAllRowsWithHeaders] = useState([])

  const saveDataTimerRef = useRef()
  const saveReviewTimerRef = useRef()
  const saveCommentTimerRef = useRef()
  const saveSelectedRowTimerRef = useRef()
  const searchTimerRef = useRef()

  /* === SAVE DATA TO SERVER DATABASE === */
  const processModifiedBy = processContext?.processData?.modified_by
  const tableSubfieldsMap = processContext?.tableSubfieldsMap

  useEffect(() => {
    let allRowsWithHeadersGlobal = tableSequenceAndHeaders.find(
      (it) => it.table_field_template_id == field.id,
    )?.sequence_header_map || [
      {
        row: 1,
        is_header: false,
        header_value: '',
      },
    ]

    setAllRowsWithHeaders(allRowsWithHeadersGlobal)
  }, [field.id, tableSequenceAndHeaders])

  useEffect(() => {
    if (
      processContext?.activityTemplate?.activity_type == ACTIVITY_TYPES.DATA_ENTRY ||
      (processContext?.activityTemplate?.activity_type == ACTIVITY_TYPES.LONG_RUNNING &&
        user.id == processContext?.processData?.modified_by) ||
      processContext?.activityTemplate?.activity_type != REVIEW_CONTEXTS.REVIEW
    ) {
      if (saveDataTimerRef.current) {
        clearTimeout(saveDataTimerRef.current)
      }

      const values = removeTableStructureData(allValues, processContext?.allFields)

      saveDataTimerRef.current = setTimeout(() => {
        ;(async () => {
          const modifiedByID =
            processContext?.activityTemplate?.activity_type === REVIEW_CONTEXTS.LONG_RUNNING
              ? processModifiedBy
              : user.id
          processDataService.saveProcessData(
            user,
            processContext?.processData?.id,
            values,
            modifiedByID,
          )
        })()
        saveDataTimerRef.current = null
      }, 3000)
    }
  }, [allValues])

  // Removed checking if user is reviewer or data-entry-guy/gal
  // To change review status to null of corresponding field changes

  useEffect(() => {
    if (
      processContext?.activityTemplate?.activity_type === REVIEW_CONTEXTS.REVIEW ||
      processContext?.activityTemplate?.activity_type === ACTIVITY_TYPES.LONG_RUNNING // Important: && user.id == processContext?.processData?.modified_by
    ) {
      if (saveReviewTimerRef.current) {
        clearTimeout(saveReviewTimerRef.current)
      }

      const values = removeTableStructureData(reviewValues, processContext?.allFields)

      saveReviewTimerRef.current = setTimeout(() => {
        ;(async () => {
          const modifiedByID =
            processContext?.activityTemplate?.activity_type === REVIEW_CONTEXTS.LONG_RUNNING
              ? processModifiedBy
              : user.id
          processDataService.saveProcessReviewData(
            user,
            processContext?.processData?.id,
            values,
            modifiedByID,
          )
        })()
        saveReviewTimerRef.current = null
      }, 3000)
    }
  }, [reviewValues])

  useEffect(() => {
    if (
      processContext?.activityTemplate?.activity_type === REVIEW_CONTEXTS.REVIEW ||
      (processContext?.activityTemplate?.activity_type === ACTIVITY_TYPES.LONG_RUNNING &&
        user.id === processContext?.processData?.modified_by)
    ) {
      if (saveCommentTimerRef.current) {
        clearTimeout(saveCommentTimerRef.current)
      }

      const values = removeTableStructureData(commentValues, processContext?.allFields)

      saveCommentTimerRef.current = setTimeout(() => {
        const modifiedByID =
          processContext?.activityTemplate?.activity_type === REVIEW_CONTEXTS.LONG_RUNNING
            ? processModifiedBy
            : user.id
        ;(async () => {
          processDataService.saveProcessCommentData(
            user,
            processContext?.processData?.id,
            values,
            modifiedByID,
          )
        })()
        saveCommentTimerRef.current = null
      }, 3000)
    }
  }, [commentValues])

  useEffect(() => {
    if (user) {
      if (saveSelectedRowTimerRef.current) {
        clearTimeout(saveSelectedRowTimerRef.current)
      }
      saveSelectedRowTimerRef.current = setTimeout(() => {
        ;(async () => {
          processDataService.saveProcessSelectableTableData(
            user,
            processContext?.processData?.id,
            selectedTableData,
            processModifiedBy,
          )
        })()
        saveSelectedRowTimerRef.current = null
      }, 3000)
    }
  }, [selectedTableData])

  /* === SAVE DATA TO SERVER DATABASE === */

  /* === AUTO COMPUTE === */

  useEffect(() => {
    if (user && activityType != ACTIVITY_TYPES.REVIEW && allValues) {
      ;(async () => {
        const computedValues = await autoComputeFieldValue(
          processContext?.allFields,
          allValues,
          listDataMetadata,
          {
            user: user,
            processPrefix:
              processContext?.activityTemplate?.prefix || processContext?.processTemplate?.prefix,
            processReferenceNum: processContext?.processData?.process_reference_num,
            processTemplateId: processContext?.processData?.process_template_id,
            selectedTableData: selectedTableData,
            tableSubfieldsMap: processContext?.tableSubfieldsMap,
            version: processContext?.processData?.version,
          },
        )
        setComputedData({ ...computedData, ...computedValues })
      })()
    }
  }, [])

  /* === AUTO COMPUTE === */

  /* === VIRTUALIZATION === */

  useEffect(() => {
    const timeout = setTimeout(() => {
      const childElement = containerRef?.current?.children[1]
      if (childElement) {
        const rowHeight = 39.1
        setElHeight(rowHeight)
        containerRef.current.children[1].setAttribute(
          'style',
          `height: ${rowHeight * allRows?.length}px`,
        )
        setEndIndex(Math.ceil(containerHeight / rowHeight))
      }
      calculatePos()
      clearTimeout(timeout)
    })
  }, [allRows])

  /* hide and show rows on scroll */
  const calculatePos = () => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }
    scrollTimeoutRef.current = setTimeout(() => {
      // do debounced work here
      const scrollTop = containerRef.current?.parentElement?.scrollTop
      const startIndex = Math.floor(scrollTop / elHeight) || 0
      setStartIndex(startIndex)
      const endIndex = Math.ceil((scrollTop + containerHeight) / elHeight)
      endIndex !== Infinity && setEndIndex(endIndex)
      clearTimeout(scrollTimeoutRef.current)
      scrollTimeoutRef.current = null
    }, 100)
  }

  /* === VIRTUALIZATION - END === */

  const handleClearFilters = () => {
    if (filterEnabled) {
      setFilterValues({})
      setFilterEnabled(false)
    } else {
      setFilterEnabled(true)
    }
  }

  /* convert the legacy data into new tableData format - values */
  useEffect(() => {
    if (columns?.length > 0 && !allValues[getTableFieldId(field.id)]) {
      const tableDataConverted = convertFromLegacyToRowWiseData(field.id, allValues, columns)
      setAllValues({
        ...allValues,
        ...tableDataConverted,
      })
    }
  }, [allValues])

  /* If Review Activity Remove Table headers without child */
  // useEffect(() => {
  //   if (activityType == ACTIVITY_TYPES.REVIEW) {
  //     const filteredTableValues = getFilteredTableData(
  //       allValues[getTableFieldId(field.id)],
  //       filterValues,
  //       listDataMetadata,
  //       columns
  //     )

  //     let allRows = []
  //     allRows = getAllRows(filteredTableValues)
  //     if (allRows.length > 0) {
  //       let allRowsWithHeadersGlobal = tableSequenceAndHeaders.find(
  //         (it) => it.table_field_template_id == field.id
  //       )?.sequence_header_map || [
  //         {
  //           row: 1,
  //           is_header: false,
  //           header_value: '',
  //         },
  //       ]
  //       // First filter: select rows that match the condition
  //       let filteredAllRowsWithHeaders = allRowsWithHeadersGlobal.filter(
  //         (it) => it.row == null || allRows.includes(parseInt(it.row))
  //       )

  //       // Second filter: remove headers without children
  //       filteredAllRowsWithHeaders = filteredAllRowsWithHeaders.filter(
  //         (it, i) =>
  //           Number.isInteger(it.row) ||
  //           (it.row == null && Number.isInteger(filteredAllRowsWithHeaders[i + 1]?.row))
  //       )

  //       setAllRowsWithHeaders(filteredAllRowsWithHeaders) // 2
  //       setTableSequenceAndHeaders(
  //         tableSequenceAndHeaders.map((it) => {
  //           if (it.table_field_template_id === field.id) {
  //             it['sequence_header_map'] = filteredAllRowsWithHeaders
  //           }
  //           return it
  //         })
  //       )
  //     }
  //   }
  // }, [allValues])

  /* get all rows of tables */
  useEffect(() => {
    if (allValues[getTableFieldId(field.id)]) {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }

      searchTimerRef.current = setTimeout(() => {
        const filteredTableValues = getFilteredTableData(
          allValues[getTableFieldId(field.id)],
          filterValues,
          listDataMetadata,
          columns,
        )

        let allRows = getAllRows(filteredTableValues) || []

        let allRowsWithHeadersGlobal = tableSequenceAndHeaders.find(
          (it) => it.table_field_template_id == field.id,
        )?.sequence_header_map || [
          {
            row: 1,
            is_header: false,
            header_value: '',
          },
        ]

        let filteredAllRowsWithHeaders = []

        if (Object.keys(filterValues).length > 0) {
          // First filter: select rows that match the condition
          filteredAllRowsWithHeaders = allRowsWithHeadersGlobal.filter(
            (it) => it.row == null || allRows.includes(parseInt(it.row)),
          )

          // Second filter: remove headers without children
          // filteredAllRowsWithHeaders = filteredAllRowsWithHeaders.filter(
          //   (it, i) =>
          //     Number.isInteger(it.row) ||
          //     (it.row == null && Number.isInteger(filteredAllRowsWithHeaders[i + 1]?.row))
          // )
        } else {
          filteredAllRowsWithHeaders = allRowsWithHeadersGlobal
        }

        setAllRowsWithHeaders(filteredAllRowsWithHeaders) // 1
        setAllRows(allRows)
        searchTimerRef.current = null
      }, 200)
    }
  }, [field, allValues, filterValues])

  /* convert the legacy review data into new tableData format - review */
  useEffect(() => {
    if (
      columns.length > 0 &&
      !reviewValues[getTableFieldId(field.id)] &&
      Object.keys(reviewValues).length > 0
    ) {
      const reviewData = convertFromLegacyReviewToRowWiseReview(
        field.id,
        allValues[getFieldId(field.id)],
        reviewValues,
        columns,
      )

      setReviewValues({ ...reviewValues, ...reviewData })
    }
  }, [reviewValues, allValues])

  /* convert the legacy comment data into new tableData format - comments */
  useEffect(() => {
    if (columns?.length > 0 && !commentValues[getTableFieldId(field.id)]) {
      const commentData = convertFromLegacyReviewToRowWiseComment(
        field.id,
        allValues[getFieldId(field.id)],
        commentValues,
        columns,
      )
      setCommentValues({ ...commentValues, ...commentData })
    }
  }, [commentValues, allValues])

  /* update page details */
  useEffect(() => {
    if (allRows?.length > 0 && columns?.length > 0) {
      setTableStructure({
        [getTableFieldId(field.id)]: {
          columns: columns,
          rows: allRows,
        },
      })
    }
  }, [allRows])

  /* auto compute */
  const triggerAutoCompute = async (allValues, listDataMetadata, processContext) => {
    const computedFields = await autoComputeFieldValue(
      processContext?.allFields.filter((it) => !it.is_subfield),
      allValues,
      listDataMetadata,
      {
        user: user,
        processPrefix:
          processContext?.activityTemplate?.prefix || processContext?.processTemplate?.prefix,
        processReferenceNum: processContext?.processData?.process_reference_num,
        processTemplateId: processContext?.processData?.process_template_id,
        selectedTableData: selectedTableData,
        tableSubfieldsMap: processContext?.tableSubfieldsMap,
        version: processContext?.processData?.version,
        dynamicListData: processContext?.dynamicListData,
      },
    )
    return computedFields
  }

  /* === TABLE ACTIONS === */
  const addNewRowInBetween = async (row, type) => {
    if (Object.values(TABLE_ADD_ROW_TYPE).includes(type)) {
      const rowsToBeAdded = 1
      const highestRowNum = allValues[getFieldId(field.id)] // this is required to maintain the rows in table

      const newRowNumber = highestRowNum + rowsToBeAdded

      const rowPositionIndex = allRowsWithHeaders.findIndex((it) => {
        return it.row == row
      })

      let newReviewValueData = {}
      let newCommentValueData = {}
      let dataToBeAdded = {}

      if (type === TABLE_ADD_ROW_TYPE.COPY_ROW) {
        const prefilledData = allValues[getTableFieldId(field.id)][getTableRowId(row)]

        dataToBeAdded = columns.reduce((acc, it) => {
          acc[getFieldId(it.id)] = prefilledData[getFieldId(it.id)]
          return acc
        }, {})
      }

      if (type === TABLE_ADD_ROW_TYPE.ADD_ROW) {
        dataToBeAdded = columns.reduce((acc, it) => {
          if (it?.default_value != null && it?.default_value.is_same_value) {
            if (it.data_structure === FIELD_TYPES.LIST_MULTI) {
              acc[getFieldId(it.id)] = it?.default_value.values[0].split(',').map(Number)
            } else {
              acc[getFieldId(it.id)] = it?.default_value.values[0]
            }
          } else {
            acc[getFieldId(it.id)] = getDefaultValueForField(it)
          }

          return acc
        }, {})
      }

      let newValueData = {
        [getTableRowId(newRowNumber)]: { ...dataToBeAdded, updatedAt: getTimeInMillis() },
      }

      let newValueLegacyData = Object.entries(dataToBeAdded).reduce((acc, [key, value]) => {
        acc[`${key}__${newRowNumber}`] = value
        return acc
      }, {})

      if (ACTIVITY_TYPES.LONG_RUNNING === activityType) {
        Object.keys(newValueLegacyData).forEach((key) => {
          let fieldId = key.split('__')[0].split('_')[1]
          let field = fields.find((it) => it.id == fieldId)
          if (
            !field?.is_read_only &&
            !field?.is_computed &&
            !field?.is_hidden &&
            field?.data_structure !== FIELD_TYPES.TABLE &&
            field?.data_structure !== FIELD_TYPES.CUSTOM_VIEW
          ) {
            let reviewKey = key.replace('f_', 'r_')
            let reviewCommentsKey = key.replace('f_', 'c_')
            newReviewValueData[reviewKey] = null
            newCommentValueData[reviewCommentsKey] = ''
          }
        })
        setReviewValues({ ...reviewValues, ...newReviewValueData })
        setCommentValues({ ...commentValues, ...newCommentValueData })
      }

      function insertAtIndex(arr, index, newRow) {
        // Split the original array into two parts: before and after the index
        const before = arr.slice(0, index)
        const after = arr.slice(index)

        // Combine the parts with the new element in between
        const newArray = [...before, { row: newRow, is_header: false, header_value: '' }, ...after]

        return newArray
      }

      let newAllRowsWithHeaders = insertAtIndex(
        allRowsWithHeaders,
        rowPositionIndex + 1,
        newRowNumber,
      )

      const finalTableData = {
        ...allValues[getTableFieldId(field.id)],
        ...newValueData,
      }

      // run auto compute after adding row
      const allValueUpdated = {
        ...allValues,
        [getFieldId(field.id)]: newRowNumber,
        [getTableFieldId(field.id)]: finalTableData,
        ...newValueLegacyData,
      }

      const computedValues = await triggerAutoCompute(
        allValueUpdated,
        listDataMetadata,
        processContext,
      )

      setAllValues({ ...allValueUpdated, ...computedValues, updatedAt: getTimeInMillis() })

      await updateTableSequenceAndHeaders(newAllRowsWithHeaders)
      setAllRows(newAllRowsWithHeaders.map((it) => it.row))
    }
  }

  const addHeader = async (row) => {
    const rowPositionIndex = allRowsWithHeaders.findIndex((it) => {
      return it.row == row
    })

    function insertAtIndex(arr, index) {
      // Split the original array into two parts: before and after the index
      const before = arr.slice(0, index)
      const after = arr.slice(index)

      // Combine the parts with the new element in between
      const newArray = [...before, { row: null, is_header: true, header_value: 'Header' }, ...after]

      return newArray
    }

    let newAllRowsWithHeaders = insertAtIndex(allRowsWithHeaders, rowPositionIndex)

    setAllValues({ updatedAt: getTimeInMillis() })
    await updateTableSequenceAndHeaders(newAllRowsWithHeaders)
    setAllRows(newAllRowsWithHeaders.filter((it) => it.row != null).map((it) => it.row))
  }

  const updateTableSequenceAndHeaders = async (newAllRowsWithHeaders) => {
    setAllRowsWithHeaders(newAllRowsWithHeaders)
    setTableSequenceAndHeaders(
      tableSequenceAndHeaders.map((it) => {
        if (it.table_field_template_id == field.id) {
          it['sequence_header_map'] = newAllRowsWithHeaders
        }
        return it
      }),
    )
    await fieldTemplateService.updateTableSequenceAndHeaders(user, {
      table_field_template_id: field.id,
      process_id: processContext?.processData?.id,
      table_sequence_and_headers: newAllRowsWithHeaders,
    })
  }

  /* adds rows upto rowsToBeAdded */

  const addNewRow = async (type) => {
    if (Object.values(TABLE_ADD_ROW_TYPE).includes(type)) {
      const rowsToBeAdded = 1
      // const highestRowNum = allValues[getFieldId(field.id)] // this is required to maintain the rows in table
      const highestRowNum = highestRowRef.current
      let rows = [...allRows]

      let newValueData = {}
      let newValueLegacyData = {}

      let newReviewValueData = {}
      let newCommentValueData = {}

      let dataToBeAdded = {}

      if (type === TABLE_ADD_ROW_TYPE.COPY_ROW) {
        dataToBeAdded =
          allValues[getTableFieldId(field.id)][
            getTableRowId(allRowsWithHeaders[allRowsWithHeaders.length - 1].row)
          ]
      }

      if (type === TABLE_ADD_ROW_TYPE.ADD_ROW) {
        dataToBeAdded = columns?.reduce((acc, column) => {
          if (column?.default_value != null && column.default_value.is_same_value) {
            if (column.data_structure === FIELD_TYPES.LIST_MULTI) {
              acc[getFieldId(column.id)] = column?.default_value.values[0].split(',').map(Number)
            } else {
              acc[getFieldId(column.id)] = column?.default_value.values[0]
            }
          } else {
            acc[getFieldId(column.id)] = getDefaultValueForField(column)
          }

          return acc
        }, {})
      }

      for (let index = highestRowNum + 1; index <= highestRowNum + rowsToBeAdded; index++) {
        rows.push(index)
        newValueData = {
          ...newValueData,
          [getTableRowId(index)]: { ...dataToBeAdded, updatedAt: getTimeInMillis() },
        }

        newValueLegacyData = {
          ...newValueLegacyData,
          ...Object.entries(dataToBeAdded || {})
            ?.filter(([key, _]) => key != 'updatedAt')
            ?.reduce((acc, [key, value]) => {
              return { ...acc, [`${key}__${index}`]: value }
            }, {}),
        }

        if (ACTIVITY_TYPES.LONG_RUNNING === activityType) {
          Object.keys(newValueLegacyData).forEach((key) => {
            let fieldId = key.split('__')[0].split('_')[1]
            let field = fields.find((it) => it.id == fieldId)
            if (
              !field?.is_read_only &&
              !field?.is_computed &&
              !field?.is_hidden &&
              field?.data_structure !== FIELD_TYPES.TABLE &&
              field?.data_structure !== FIELD_TYPES.CUSTOM_VIEW
            ) {
              let reviewKey = key.replace('f_', 'r_')
              let reviewCommentsKey = key.replace('f_', 'c_')
              newReviewValueData[reviewKey] = null
              newCommentValueData[reviewCommentsKey] = ''
            }
          })
          setReviewValues({ ...reviewValues, ...newReviewValueData })
          setCommentValues({ ...commentValues, ...newCommentValueData })
        }
      }

      const finalTableData = {
        ...allValues[getTableFieldId(field.id)],
        ...newValueData,
      }

      // run auto compute after adding row
      const allValueUpdated = {
        ...allValues,
        [getFieldId(field.id)]: highestRowNum + rowsToBeAdded,
        [getTableFieldId(field.id)]: finalTableData,
        ...newValueLegacyData,
      }

      const computedValues = await triggerAutoCompute(
        allValueUpdated,
        listDataMetadata,
        processContext,
      )

      setAllValues({ ...allValueUpdated, ...computedValues, updatedAt: getTimeInMillis() })
      await updateTableSequenceAndHeaders([
        ...allRowsWithHeaders,
        { row: highestRowNum + rowsToBeAdded, is_header: false, header_value: '' },
      ])

      setAllRows(Array.from(new Set(rows)))
    }
  }

  const isRowSelected = (rowId) => {
    return currentSelectedRowData?.includes(String(rowId)) || false
  }

  const handleRowSelect = useCallback(
    async (row, index, currentRowData) => {
      // Here We commenting  the default Value  logic for select/unselect rows
      // const editableFields = columns.filter((it) => !it.is_read_only && !it.is_imported_field)
      let rowData = {}

      /* handle selection state */
      const isSelected = isRowSelected(row)
      if (isSelected) {
        unSelectRow(field.id, String(row))
      } else {
        selectRow(field.id, String(row))
      }

      /* handle value change */
      // editableFields?.forEach((field) => {
      //   rowData = {
      //     ...rowData,
      //     [getFieldId(field.id)]: getDefaultValueForField(field),
      //   }
      // })

      await updateRowData(index, row, { ...currentRowData, ...rowData })
    },
    [currentSelectedRowData, allValues],
  )

  let deleteRowValues = (tableId, rowNo, keys = [], allValues) => {
    const values = { ...allValues }

    // removing all legacy value from allValues
    keys.forEach((key) => {
      delete values[key]
    })

    // delete row data from tabular data
    const tableValues = { ...allValues[getTableFieldId(tableId)] }
    delete tableValues[getTableRowId(rowNo)]

    return {
      ...values,
      [getTableFieldId(tableId)]: tableValues,
    }
  }

  const handleRowDelete = useCallback(
    async (row) => {
      // get all legacy key-value pair
      const allKeysToBeRemoved = columns.map((it) => getLegacySubfieldId(it.id, row))

      let updatedAllValues = { ...allValues }

      /* getting allValues after removing */
      const rowsLeft = allRows.filter((it) => it != row)
      const updatedAllRowsWithHeaders = allRowsWithHeaders.filter((it) => it.row != row)

      setAllRows(rowsLeft)
      updatedAllValues = deleteRowValues(field.id, row, allKeysToBeRemoved, updatedAllValues)

      /* run computation to re-evaluate computed field values */
      const computedFields = await autoComputeFieldValue(
        processContext?.allFields,
        { ...updatedAllValues },
        listDataMetadata,
        {
          user: user,
          processPrefix:
            processContext?.activityTemplate?.prefix || processContext?.processTemplate?.prefix,
          processReferenceNum: processContext?.processData?.process_reference_num,
          processTemplateId: processContext?.processData?.process_template_id,
          selectedTableData: selectedTableData,
          tableSubfieldsMap: processContext?.tableSubfieldsMap,
          version: processContext?.processData?.version,
        },
      )

      setInitialAllValues({ ...updatedAllValues, ...computedFields, updatedAt: getTimeInMillis() })
      await updateTableSequenceAndHeaders(updatedAllRowsWithHeaders)
    },
    [allValues, allRows, allRowsWithHeaders],
  )

  const handleHeaderDelete = async (index) => {
    let updatedAllRowsWithHeaders = allRowsWithHeaders.filter((_, i) => index != i)

    setAllValues({ updatedAt: getTimeInMillis() })
    await updateTableSequenceAndHeaders(updatedAllRowsWithHeaders)

    setAllRows(updatedAllRowsWithHeaders.filter((it) => it.row != null).map((it) => it.row))
  }

  /* === TABLE ACTIONS END=== */

  /* hide show columns */
  useEffect(() => {
    ;(async () => {
      user &&
        hiddenColumns &&
        fetchedConfig &&
        (await saveUserTableConfigs(user, TABLE_CONFIG_CONTEXTS.HIDDEN_COLUMN, hiddenColumns, null))
    })()
  }, [hiddenColumns])

  useEffect(() => {
    ;(async () => {
      await getUserTableConfigs(user)
    })()
  }, [])

  /* update when there is any changes in columns for respective different views  or accumulated fields*/
  const saveUserTableConfigs = async (
    user,
    tableConfigContext,
    columns = [],
    accumulatedColumn,
  ) => {
    try {
      return await userConfig.saveTableConfig(
        user,
        field.id,
        columns,
        TABLE_CONTEXTS.DATA_ENTRY_VIEW,
        processContext?.activityTemplate?.activity_type,
        tableConfigContext,
        accumulatedColumn,
      )
    } catch (error) {
      console.error(error)
    }
  }

  const getUserTableConfigs = async (user) => {
    try {
      let response = (await userConfig.getTableConfig(user)).data
      /* set hidden ids */
      const hiddenColumnsRemote = getHiddenId(
        response,
        TABLE_CONTEXTS.DATA_ENTRY_VIEW,
        TABLE_CONFIG_CONTEXTS.HIDDEN_COLUMN,
        field.id,
        ACTIVITY_TYPES.DATA_ENTRY,
      )
      setFetchedConfig(true)
      setHiddenColumns(hiddenColumnsRemote)

      /* set freeze ids */
      const freezeColumnsRemote = getHiddenId(
        response,
        TABLE_CONTEXTS.DATA_ENTRY_VIEW,
        TABLE_CONFIG_CONTEXTS.VERTICAL_FREEZE,
        field.id,
        ACTIVITY_TYPES.DATA_ENTRY,
      )

      setFreezeColumnId(freezeColumnsRemote)

      return response
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (user && freezeColumnId) {
      ;(async () => {
        await saveUserTableConfigs(
          user,
          TABLE_CONFIG_CONTEXTS.VERTICAL_FREEZE,
          freezeColumnId,
          null,
        )
      })()
    }
  }, [freezeColumnId])

  const handleFreeze = (path) => {
    const numericPath = Array.isArray(path) ? path.map((item) => Number(item)) : [Number(path)]
    setFreezeColumnId(numericPath)
  }

  /* =============== START ============= */

  const triggerAutoComputeRowWise = async (
    allValues,
    listDataMetadata,
    processContext,
    computableFields,
    index,
    row,
  ) => {
    const computedFields = await autoComputeFieldValue(
      processContext?.allFields,
      allValues,
      listDataMetadata,
      {
        user: user,
        processPrefix:
          processContext?.activityTemplate?.prefix || processContext?.processTemplate?.prefix,
        processReferenceNum: processContext?.processData?.process_reference_num,
        processTemplateId: processContext?.processData?.process_template_id,
        selectedTableData: selectedTableData,
        tableSubfieldsMap: processContext?.tableSubfieldsMap,
        tableRow: row,
        slNo: index + 1,
        version: processContext?.processData?.version,
        allProcessData: processContext?.allProcessData,
        dynamicListData: processContext?.dynamicListData,
      },
      computableFields,
    )
    return computedFields
  }

  const updateRowData = async (index, row, allValueUpdated, aggregationTableValues = {}) => {
    /* trigger auto computed fields - first level*/
    const autoComputeFieldValues = await triggerAutoComputeRowWise(
      { ...allValues, ...allValueUpdated },
      listDataMetadata,
      processContext,
      columns.filter((it) => it.is_computed),
      index,
      row,
    )

    /* trigger auto computed fields - second level*/
    const autoComputeFieldValues2 = await triggerAutoComputeRowWise(
      { ...allValues, ...allValueUpdated, ...autoComputeFieldValues },
      listDataMetadata,
      processContext,
      columns.filter((it) => it.is_computed),
      index,
      row,
    )

    // convert tabular data into legacy format
    const aggregationTableSequence = []
    const aggregatedTableData = Object.entries(
      aggregationTableValues?.transformedData || {},
    ).reduce((acc, [key, value]) => {
      const newKey = key.replace('tf_', 'f_') // Convert tf_ to f_

      let tableSequence = []
      const newValue = Object.entries(value).reduce((innerAcc, [innerKey, innerValue]) => {
        Object.entries(innerValue).forEach(([fieldKey, fieldValue]) => {
          if (fieldKey !== 'updatedAt') {
            // Ignore updatedAt field
            innerAcc[`${fieldKey}__${innerKey.replace('tr_', '')}`] = fieldValue
          }
        })
        tableSequence.push({
          row: parseInt(innerKey.replace('tr_', '')),
          is_header: false,
          header_value: '',
        })
        return innerAcc
      }, {})

      acc[newKey] = Object.keys(value).length // Count the number of tr_ entries and assign to f_key
      aggregationTableSequence.push({
        table_field_template_id: parseInt(key.replace('tf_', '')),
        sequence_header_map: tableSequence,
        process_id: processContext?.processData?.id,
      })
      return { ...acc, ...newValue }
    }, {})

    if (aggregationTableSequence.length > 0) {
      let updatedTableSequenceAndHeaders = tableSequenceAndHeaders.reduce((acc, value) => {
        let aggregatedTableData = aggregationTableSequence.find(
          (it) =>
            it.table_field_template_id == value.table_field_template_id &&
            it.process_id == value.process_id,
        )
        if (aggregatedTableData) {
          value['sequence_header_map'] = aggregatedTableData.sequence_header_map
        }

        acc.push(value)
        return acc
      }, [])
      setTableSequenceAndHeaders(updatedTableSequenceAndHeaders)
      await fieldTemplateService.updateMultipleTableSequenceAndHeaders(user, {
        tableSequenceAndHeaders: aggregationTableSequence,
      })
    }

    const finalUpdatedAggeragationData = {
      ...aggregatedTableData,
      ...aggregationTableValues.transformedData,
    }

    // append converted data to legacyConvertedData
    // pass the subfieldIds to updateRowValues

    // separate fields outside rows in auto compute
    let rowsComputedData = {}
    let otherComputedData = {}
    const columnIds = columns.map((it) => it.id + '')
    Object.entries(autoComputeFieldValues2).forEach(([key, value]) => {
      if (columnIds.includes(key.split('_')[1])) {
        rowsComputedData = {
          ...rowsComputedData,
          [key]: value,
        }
      } else {
        otherComputedData = {
          ...otherComputedData,
          [key]: value,
        }
      }
    })

    const finalUpdatedRowValues = {
      ...allValueUpdated,
      ...rowsComputedData,
    }

    // convert column data to legacy data
    const legacyConvertedData = Object.entries(finalUpdatedRowValues)
      .filter(([key, _]) => key != 'updatedAt')
      .reduce((acc, [key, value]) => {
        return { ...acc, [key + '__' + row]: value }
      }, {})

    // updated row values
    updateRowValues(
      getTableFieldId(field.id),
      getTableRowId(row),
      finalUpdatedRowValues,
      { ...legacyConvertedData, ...finalUpdatedAggeragationData },
      aggregationTableValues.subfieldIds,
    )
  }

  useEffect(() => {
    // computation of normal computed fields
    if (ACTIVITY_TYPES.DATA_ENTRY == activityType) {
      ;(async () => {
        const columnIds = columns.map((it) => it.id + '')

        // this will calculate for column sum field
        const normalComputedFieldValues = await triggerAutoComputeRowWise(
          allValues,
          listDataMetadata,
          processContext,
          processContext?.allFields?.filter(
            (it) => !columnIds.includes(it.id + '') && it.is_computed,
          ),
        )

        // this calculate field based on column sum
        const normalComputedFieldValues1 = await triggerAutoComputeRowWise(
          { ...allValues, ...normalComputedFieldValues },
          listDataMetadata,
          processContext,
          processContext?.allFields?.filter(
            (it) => !columnIds.includes(it.id + '') && it.is_computed,
          ),
        )

        // final save auto computed value to store
        setAllValues(normalComputedFieldValues1)
      })()
    }
  }, [allValues.updatedAt])

  const handleDisableConditionalEnableDisable = (subfieldId, conditions, rowNum, rowValues) => {
    let rowValuesCopy = _.cloneDeep(rowValues)

    conditions
      .filter((condition) => {
        return (
          condition.is_destination_field_subfield &&
          condition.is_source_field_subfield &&
          condition.source_field_template_id == subfieldId
        )
      })
      .forEach((condition) => {
        const {
          source_field_template_id,
          destination_field_template_id,
          enable_on_value,
          operation,
        } = condition

        let dv = null

        const isEnabled = constraintValidationWithOperation(
          rowValuesCopy[getFieldId(source_field_template_id)],
          enable_on_value,
          operation,
        )

        if (isEnabled) {
          dv = getSubfieldDefaultValue(
            {
              data_structure: condition.destination_data_structure,
              default_value: condition.destination_field_default_value,
            },
            Number(rowNum) - 1,
          )
        } else {
          dv = getDefaultValueForField({
            data_structure: condition.destination_data_structure,
          })
        }

        // update the destination value to default
        rowValuesCopy[getFieldId(destination_field_template_id)] = dv
      })

    return rowValuesCopy
  }

  const aggregationTableData = (fields, allValues) => {
    let transformedData = {}
    let subfieldIds = []

    const getGroupByAndAccumulatorFieldIds = (tableFieldTemplateId) => {
      const relatedSubfields = tableSubfieldsMap.filter(
        (subfield) => subfield.table_field_template_id === tableFieldTemplateId,
      )

      const groupByFieldTemplateIds = relatedSubfields
        .map(
          ({ parent_table_group_by_subfield_template_id }) =>
            parent_table_group_by_subfield_template_id,
        )
        .filter(Boolean)

      const accumulatorFieldTemplateIds = relatedSubfields
        .map(
          ({ parent_table_accumulator_subfield_template_id }) =>
            parent_table_accumulator_subfield_template_id,
        )
        .filter(Boolean)

      const combinedFieldTemplateIds = [...groupByFieldTemplateIds, ...accumulatorFieldTemplateIds]
      return { groupByFieldTemplateIds, accumulatorFieldTemplateIds, combinedFieldTemplateIds }
    }

    for (const field of fields) {
      if (field.is_aggregation_table) {
        const aggregationTableId = field.id
        const parentTableId = field.aggregation_parent_table_field_template_id

        const { groupByFieldTemplateIds, accumulatorFieldTemplateIds, combinedFieldTemplateIds } =
          getGroupByAndAccumulatorFieldIds(aggregationTableId)

        subfieldIds.push(combinedFieldTemplateIds)

        let curTransformedData
        if (parentTableId) {
          const relatedSubfields = tableSubfieldsMap
            .filter((subfield) => subfield.table_field_template_id === aggregationTableId)
            .map((subfield) => subfield.subfield_template_id)

          const subfieldToTemplateIdMap = Object.fromEntries(
            relatedSubfields.map((subfieldId, index) => [
              subfieldId,
              combinedFieldTemplateIds[index] || null,
            ]),
          )

          const parentTableData = {
            [getTableFieldId(parentTableId)]: allValues[getTableFieldId(parentTableId)],
          }
          const groupedData = []

          for (const group of Object.values(parentTableData)) {
            for (const item of Object.values(group)) {
              let existingGroup = groupedData.find((existingItem) =>
                groupByFieldTemplateIds.every(
                  (fieldId) => existingItem[getFieldId(fieldId)] === item[getFieldId(fieldId)],
                ),
              )

              if (existingGroup) {
                accumulatorFieldTemplateIds.forEach((accFieldId) => {
                  const accField = getFieldId(accFieldId)
                  existingGroup[accField] += item[accField]
                })
              } else {
                groupedData.push({ ...item })
              }
            }
          }

          curTransformedData = groupedData.reduce((acc, obj, index) => {
            const transformedObj = {
              updatedAt: obj.updatedAt,
              ...Object.entries(subfieldToTemplateIdMap).reduce(
                (innerAcc, [subfieldKey, templateId]) => {
                  innerAcc[`f_${subfieldKey}`] = obj[`f_${templateId}`]
                  return innerAcc
                },
                {},
              ),
            }
            acc[`tr_${index + 1}`] = transformedObj
            return acc
          }, {})
        }

        transformedData[getTableFieldId(aggregationTableId)] = curTransformedData
      }
    }

    return { transformedData, subfieldIds }
  }

  const onChangeValue = async (index, row, id, value, dataType) => {
    switch (dataType) {
      case ON_CHANGE_DATA_TYPE.REVIEW_DATA:
        let reviewValueUpdated = {
          ...reviewData[getTableRowId(row)],
          [getReviewId(id)]: value,
        }

        /* sync child to maintain parent-child relationship */
        reviewValueUpdated = syncChildParentReviewRecursively(
          id,
          value,
          processContext?.fieldTemplateDep,
          reviewValueUpdated,
        )

        reviewValueUpdated = { ...reviewData[getTableRowId(row)], ...reviewValueUpdated }

        /* persist value */
        updateReviewDataRow(getTableFieldId(field.id), row, reviewValueUpdated)
        break
      case ON_CHANGE_DATA_TYPE.COMMENT_DATA:
        let commentValueUpdated = {
          ...commentData[getTableRowId(row)],
          [getCommentId(id)]: value,
        }
        updateCommentDataRow(getTableFieldId(field.id), row, commentValueUpdated)
        break
      default:
        // Important: Set cell review status to null to set for review
        if (activityType === ACTIVITY_TYPES.LONG_RUNNING) {
          setReviewValues({ ...reviewValues, [getLegacySubfieldReviewId(id, row)]: null })
        }

        let rowValueUpdated = {
          ...allValues[getTableFieldId(field.id)][getTableRowId(row)],
          [getFieldId(id)]: value,
        }

        rowValueUpdated = handleDisableConditionalEnableDisable(
          id,
          conditionalEnableDisable,
          row,
          rowValueUpdated,
        )

        /* update value data */
        let allValueUpdated = {
          ...rowValueUpdated,
          [getFieldId(id)]: value,
        }

        /* sync child to maintain parent-child relationship */
        allValueUpdated = syncChildRecursively(
          columns,
          fieldTemplateDep,
          listDataDep,
          userTeamMap,
          allValueUpdated,
        )

        // check for field validation constraints
        const error = checkFieldConstraints(
          columns.find((it) => it.id == id),
          value,
          fieldConstraints,
          allValues,
          row,
        )

        setFieldValidationError({
          ...fieldValidationError,
          ...error,
        })

        const xyz = {
          ...allValues,
        }

        xyz[getTableFieldId(field.id)][getTableRowId(row)] = allValueUpdated

        // finally -> updateRowData
        const aggregationTableValues = aggregationTableData(fields, xyz)

        await updateRowData(index, row, allValueUpdated, aggregationTableValues)
    }
  }

  const isFieldDisabled = useCallback(
    (column, rowId, isSelected, isImported, previousProcessReviewData, rowReview) => {
      let isDisabled = false

      /* checking final/enable disable */
      if (column.is_read_only || activityType == ACTIVITY_TYPES.REVIEW) {
        isDisabled = true
      } else {
        if (activityType === ACTIVITY_TYPES.LONG_RUNNING && version === 1) {
          // checking if conditionally disabled
          if (column?.conditional_enable_disable && !isDisabled) {
            const isConditionallyDisabled = !isConditionallyEnabled(
              column?.id,
              allValues,
              conditionalEnableDisable,
              allFields,
              rowId,
            )

            isDisabled = isConditionallyDisabled
          }

          // finally, if conditionally not disabled, check if by review status, it's disabled or not
          if (!isDisabled) {
            const reviewStatus = rowReview && rowReview[getReviewId(column.id)]
            if (reviewStatus === true) {
              isDisabled = true
            } else {
              isDisabled = false
            }
          }
        } else {
          // checking if conditionally disabled
          if (column?.conditional_enable_disable && !isDisabled) {
            const isConditionallyDisabled = !isConditionallyEnabled(
              column?.id,
              allValues,
              conditionalEnableDisable,
              allFields,
              rowId,
            )

            isDisabled = isConditionallyDisabled
          }

          // finally, if conditionally not disabled, check if by review status it's disabled or not
          if (!isDisabled) {
            const reviewStatus =
              previousProcessReviewData && previousProcessReviewData[getReviewId(column.id)]
            isDisabled = reviewStatus
          }
        }
      }

      /* selectable table enable/disable */
      if (isImported) {
        isDisabled = isSelected ? isDisabled : true
      } else {
        isDisabled = disabled ? disabled : isDisabled
      }

      /* add disabled field in noValidation */
      if (activityType != ACTIVITY_TYPES.REVIEW) {
        if (isDisabled) {
          setNoValidationValues(getLegacySubfieldId(column.id, rowId))
        } else {
          removeFromNoValidationValues(getLegacySubfieldId(column.id, rowId))
        }
      }

      return isDisabled
    },
    [allValues, currentSelectedRowData],
  )

  const isHiddenCell = useCallback(
    (field, row) => {
      const isHidden = isFieldHidden(field, processContext?.hiddenFields, hiddenColumns, {
        allValues,
        conditionalEnableDisable,
        fields: allFields,
        row,
      })

      // const key = getLegacySubfieldId(field.id, row)
      // if (isHidden) {
      //   const noValidationValues = getNoValidationValues()
      //   let noValidationArrayValues = [...new Set(noValidationValues)]
      //   if (!noValidationArrayValues.includes(key)) {
      //     setNoValidationValues(key)
      //   }
      // } else {
      //   const noValidationValues = getNoValidationValues()
      //   let noValidationArrayValues = [...new Set(noValidationValues)]
      //   if (noValidationArrayValues.includes(key)) {
      //     removeFromNoValidationValues(key)
      //   }
      // }

      return isHidden
    },
    [allValues, hiddenColumns, conditionalEnableDisable],
  )

  const syncWithParentCell = useCallback(
    (column, rowData, listData, teamsList) => {
      const allValues = getAllValues()
      const options = syncWithParent(
        column.id,
        fieldTemplateDep,
        listDataDep,
        allValues,
        listData,
        teamsList,
        {
          userTeamMap,
          field: column,
          allValues: rowData,
        },
      )
      return options || []
    },
    [formUpdatedAt],
  )

  /* =============== END ============= */

  const isReviewState = useCallback(() => {
    return (
      (ACTIVITY_TYPES.LONG_RUNNING === activityType &&
        user.teams.some((team) =>
          (activityTemplate?.lrp_reviewer_team_ids ?? []).includes(team),
        )) ||
      activityType == ACTIVITY_TYPES.REVIEW
    )
  }, [activityTemplate?.lrp_reviewer_team_ids, activityType, user.teams])

  const getReviewValues = useCallback(
    (tableId, row) => {
      const currentRowReviewValues = reviewValues[getTableFieldId(tableId)]
        ? reviewValues[getTableFieldId(tableId)][getTableRowId(row)]
        : isReviewState()
          ? { updatedAt: getTimeInMillis() }
          : {}
      return currentRowReviewValues
    },
    [isReviewState, reviewValues],
  )

  const getDataValues = useCallback(
    (tableId, row) => {
      const currentRowAllValues = allValues[getTableFieldId(tableId)]
        ? allValues[getTableFieldId(tableId)][getTableRowId(row)]
        : !isReviewState()
          ? { updatedAt: getTimeInMillis() }
          : {}
      return currentRowAllValues
    },
    [allValues],
  )

  const getCommentValues = useCallback(
    (tableId, row) => {
      const currentRowCommentValues = commentValues[getTableFieldId(tableId)]
        ? commentValues[getTableFieldId(tableId)][getTableRowId(row)]
        : isReviewState()
          ? { updatedAt: getTimeInMillis() }
          : {}
      return currentRowCommentValues
    },
    [commentValues, isReviewState],
  )

  const saveExcelData = async (data, sequenceAndHeaders, needCleanInsert) => {
    // here, we have to find if we need to partially replace the existing key-value or remove existing and comping data
    // if partial, remove existing table data (tabular data and normalized data)

    const columnSet = new Map()
    columns.forEach((it) => {
      columnSet.set(it.id, it.id)
    })

    const allValuesWithoutExistingTableData = Object.keys(allValues).reduce((acc, cur) => {
      const fieldId = getFieldIdFromKey(cur).id
      const isColumnKeyFromTable = columnSet.has(fieldId)
      if (!isColumnKeyFromTable && cur !== `tf_${field.id}`) {
        return { ...acc, [cur]: allValues[cur] }
      }

      return acc
    }, {})

    let finalData
    if (needCleanInsert) {
      // Excel data is source of truth
      // Remove existing table and add fresh data
      finalData = {
        ...allValuesWithoutExistingTableData,
        ...data,
        updatedAt: getTimeInMillis(),
      }
    } else {
      // Append excel data at the end of existing data
      finalData = { ...allValues, ...data, updatedAt: getTimeInMillis() }
    }

    setInitialAllValues(finalData)
    await updateTableSequenceAndHeaders(sequenceAndHeaders)
    setOpenExcelUpload(false) // finally close the dialog
  }

  return (
    <>
      <Toolbar
        className="pl-0"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: '32px !important',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            top: '-7px',
            gap: 2,
            maxHeight: '16px !important',
          }}
        >
          <Typography
            className="textPrimary fontWeight700 "
            sx={{
              fontSize: dataEntryPageTitle,
              lineHeight: 1.2,
            }}
            style={{ fontSize: dataEntryPageTitle }}
          >
            {field?.field_template_name}
          </Typography>

          <TableSetting
            className="table-preview"
            columns={getFieldForSetting(columns)}
            activityType={processContext?.activityTemplate?.activity_type}
            hiddenColumns={hiddenColumns || []}
            setHiddenColumns={setHiddenColumns}
            handleFreeze={handleFreeze}
            freezeColumnId={freezeColumnId}
          />
          <Box display="flex" alignItems="center" sx={{ gap: 0.5 }}>
            <Tooltip title={filterEnabled ? 'Clear Filter' : 'Enable Filter'}>
              <IconButton
                size="small"
                onClick={handleClearFilters}
                sx={{ padding: 0, display: 'flex', alignItems: 'center', marginLeft: '-5px' }}
              >
                {filterEnabled ? (
                  <FilterAltOffOutlinedIcon sx={{ fontSize: 19 }} />
                ) : (
                  <FilterAltOutlinedIcon sx={{ fontSize: 19 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {(processContext?.activityTemplate?.activity_type == ACTIVITY_TYPES.DATA_ENTRY ||
          (processContext?.activityTemplate?.activity_type == ACTIVITY_TYPES.LONG_RUNNING &&
            user.id == processContext?.processData?.modified_by)) &&
          field.allow_excel_upload && (
            <Box>
              <IconButton size="small" onClick={() => setOpenExcelUpload(true)}>
                <UploadFile fontSize="12px" />
              </IconButton>
            </Box>
          )}
      </Toolbar>
      {allRowsWithHeaders?.length >= MIN_ROWS_TO_VIRTUALIZE_TABLE ? (
        <TableContainer
          className="forminput-table-formate"
          component={Paper}
          sx={{
            width: '100%',
            marginBottom: '20px',
            height: '400px',
            position: 'relative',
          }}
          onScroll={calculatePos}
        >
          {/* virtualized  table */}
          <Table
            className="responsiveTable workontask-table"
            id={getFieldId(field.id)}
            ref={containerRef}
            onWheel={calculatePos}
          >
            <TableHeading
              style={{ position: 'sticky', top: -1 }}
              columns={columns}
              hiddenColumnIds={hiddenColumns}
              width={containerRef?.current?.offsetWidth}
              rows={allRowsWithHeaders}
              setFilterValues={setFilterValues}
              filterValues={filterValues}
              filterEnabled={filterEnabled}
              isSelectable={field.is_selectable_table}
              fields={fields}
              freezeColumnId={freezeColumnId}
              field={field}
            />
            <TableBody
              sx={{
                position: 'relative',
                width: containerRef?.current?.offsetWidth,
              }}
            >
              {/* rendered rows */}
              {allRowsWithHeaders &&
                allValues[getTableFieldId(field.id)] &&
                allRowsWithHeaders.slice(startIndex, endIndex).map((it, index) => {
                  if (it.is_header) {
                    return (
                      <TableRowHeader
                        key={'header_' + startIndex + index}
                        headerValue={it.header_value}
                        columnsLength={columns.length}
                        index={startIndex + index}
                        style={{
                          position: 'absolute',
                          top: (index + startIndex) * elHeight,
                        }}
                        columnWidth={containerRef?.current?.offsetWidth}
                        allRowsWithHeaders={allRowsWithHeaders}
                        updateTableSequenceAndHeaders={updateTableSequenceAndHeaders}
                        setAllValues={setAllValues}
                        handleHeaderDelete={handleHeaderDelete}
                        disabled={Object.keys(filterValues).length > 0}
                        isSelectable={field.is_selectable_table}
                        allowTableHeaders={field.allow_table_headers}
                      />
                    )
                  }
                  return (
                    <TableRowWrapper
                      freezeColumnId={freezeColumnId}
                      index={startIndex + index}
                      key={getTableRowId(it) + '_' + startIndex + index}
                      style={{
                        width: containerRef?.current?.offsetWidth,
                        position: 'absolute',
                        top: (index + startIndex) * elHeight,
                      }}
                      columnWidth={containerRef?.current?.offsetWidth}
                      row={it.row}
                      rows={allRows}
                      isImported={field.is_imported_field}
                      isSelectable={field.is_selectable_table}
                      allowRowDelete={field.allow_delete_rows}
                      allowAddRows={field.allow_add_rows}
                      allowTableHeaders={field.allow_table_headers}
                      isSelected={isRowSelected(it.row)}
                      columns={columns}
                      hiddenColumnIds={hiddenColumns}
                      disabled={disabled}
                      currentRowData={getDataValues(field.id, it.row)}
                      currentReviewDataRow={getReviewValues(field.id, it.row)}
                      currentCommentDataRow={getCommentValues(field.id, it.row)}
                      handleRowDataChange={(id, value, dataType) =>
                        onChangeValue(startIndex + index, it.row, id, value, dataType)
                      }
                      isFieldDisabled={isFieldDisabled}
                      isHidden={isHiddenCell}
                      handleRowSelect={handleRowSelect}
                      handleRowDelete={() => handleRowDelete(it.row)}
                      normalFieldLastUpdateAt={allValues?.updatedAt}
                      reviewLastUpdatedAt={reviewValues?.updatedAt}
                      commentLastUpdatedAt={commentValues?.updatedAt}
                      syncWithParent={syncWithParentCell}
                      addNewRowInBetween={addNewRowInBetween}
                      addHeader={addHeader}
                      allRowsWithHeaders={allRowsWithHeaders}
                      optionsDisabled={Object.keys(filterValues).length > 0}
                    />
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer
          className="forminput-table-formate mb-2"
          component={Paper}
          sx={{
            width: '100%',
            marginBottom: '20px',
            maxHeight: '400px',
            position: 'relative',
            boxShadow: '1px 2px 6px 0px rgb(57 85 120 / 11%)',
          }}
        >
          {/* normal Table */}
          <Table
            className="responsiveTable workontask-table"
            id={`f_${field.id}`}
            ref={containerRef}
          >
            <TableHeading
              style={{ position: 'sticky', top: 0 }}
              columns={columns}
              hiddenColumnIds={hiddenColumns}
              width={containerRef?.current?.offsetWidth}
              rows={allRowsWithHeaders}
              setFilterValues={setFilterValues}
              filterValues={filterValues}
              filterEnabled={filterEnabled}
              isSelectable={field.is_selectable_table}
              fields={fields}
              freezeColumnId={freezeColumnId}
              field={field}
            />
            <TableBody
              sx={{
                position: 'relative',
                width: containerRef?.current?.offsetWidth,
              }}
            >
              {/* rendered rows */}
              {allRowsWithHeaders &&
                allValues[getTableFieldId(field.id)] &&
                allRowsWithHeaders.map((it, index) => {
                  if (it.is_header) {
                    return (
                      <TableRowHeader
                        key={'header_' + index}
                        headerValue={it.header_value}
                        columnsLength={columns.length}
                        index={index}
                        allRowsWithHeaders={allRowsWithHeaders}
                        updateTableSequenceAndHeaders={updateTableSequenceAndHeaders}
                        setAllValues={setAllValues}
                        isSelectable={field.is_selectable_table}
                        handleHeaderDelete={handleHeaderDelete}
                        disabled={Object.keys(filterValues).length > 0}
                        allowTableHeaders={field.allow_table_headers}
                      />
                    )
                  }

                  return (
                    <TableRowWrapper
                      freezeColumnId={freezeColumnId}
                      index={index}
                      key={getTableRowId(it.row) + '_' + index}
                      style={{
                        width: containerRef?.current?.offsetWidth,
                      }}
                      columnWidth={containerRef?.current?.offsetWidth}
                      row={it.row}
                      rows={allRows}
                      isImported={field.is_imported_field}
                      isSelectable={field.is_selectable_table}
                      allowRowDelete={field.allow_delete_rows}
                      allowAddRows={field.allow_add_rows}
                      allowTableHeaders={field.allow_table_headers}
                      isSelected={isRowSelected(it.row)}
                      columns={columns}
                      hiddenColumnIds={hiddenColumns}
                      disabled={disabled}
                      currentRowData={getDataValues(field.id, it.row)}
                      currentReviewDataRow={getReviewValues(field.id, it.row)}
                      currentCommentDataRow={getCommentValues(field.id, it.row)}
                      handleRowDataChange={(id, value, dataType) =>
                        onChangeValue(startIndex + index, it.row, id, value, dataType)
                      }
                      isFieldDisabled={isFieldDisabled}
                      isHidden={isHiddenCell}
                      handleRowSelect={handleRowSelect}
                      handleRowDelete={() => handleRowDelete(it.row)}
                      normalFieldLastUpdateAt={allValues?.updatedAt}
                      reviewLastUpdatedAt={reviewValues?.updatedAt}
                      commentLastUpdatedAt={commentValues?.updatedAt}
                      syncWithParent={syncWithParentCell}
                      addNewRowInBetween={addNewRowInBetween}
                      addHeader={addHeader}
                      allRowsWithHeaders={allRowsWithHeaders}
                      optionsDisabled={Object.keys(filterValues).length > 0}
                    />
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TableFooter
        onAddRow={addNewRow}
        totalRows={allRows?.length || 0}
        disabled={disabled || !field.allow_add_rows}
      />
      {openExcelUpload && (
        <UploadExcelDataExtended
          columns={columns}
          field={field}
          isOpen={openExcelUpload}
          existingTabularData={allValues[getTableFieldId(field.id)]}
          tableSequenceAndHeadersExisting={
            tableSequenceAndHeaders.find((it) => it.table_field_template_id == field.id)
              ?.sequence_header_map
          }
          hiddenColumnIds={hiddenColumns}
          onClose={() => setOpenExcelUpload(false)}
          triggerAutoComputeRowWise={(values, index, row) =>
            triggerAutoComputeRowWise(
              values,
              listDataMetadata,
              processContext,
              columns.filter((it) => it.is_computed),
              index,
              row,
            )
          }
          syncChildRecursively={(allValueUpdated) =>
            syncChildRecursively(
              columns,
              fieldTemplateDep,
              listDataDep,
              userTeamMap,
              allValueUpdated,
            )
          }
          onSave={saveExcelData}
          isFieldDisabled={isFieldDisabled}
        />
      )}
    </>
  )
}

export default memo(TableField, (prevProps, nextProps) => prevProps.field.id === nextProps.field.id)
